// Общая инициализация Telegram Mini App: полноэкранный режим (Bot API 8.0+)
// и верхний отступ под системный оверлей Telegram (contentSafeAreaInset).
// Вне Telegram (обычный браузер) всё отключается, отступ = 0.
// ВАЖНО: tg.isFullscreen на части клиентов не обновляется, поэтому полагаемся
// на факт вызова requestFullscreen, а не на флаг.

import axios from "axios";
import { API } from "@/utils/api";
import { getClientKey, grantAccess } from "@/utils/access";
import { setUserToken } from "@/utils/auth";

let topInset = 0;
let fullscreenWanted = false;
let tgRef = null;
const listeners = new Set();
let initialized = false;

const emit = () => listeners.forEach((fn) => fn(topInset));

// Дополнительный буфер, чтобы плавающая кнопка Telegram (свернуть/«...»)
// не перекрывала наш интерфейс — опускаем контент чуть ниже safe area.
const EXTRA_TOP = 24;

const readInset = () => {
  try {
    const v = tgRef?.contentSafeAreaInset?.top ?? tgRef?.safeAreaInset?.top ?? 0;
    return (v || 60) + EXTRA_TOP; // страховка: оверлей Telegram в fullscreen ~59-62px
  } catch {
    return 60 + EXTRA_TOP;
  }
};

const applyInset = () => {
  const v = fullscreenWanted ? readInset() : 0;
  if (v !== topInset) {
    topInset = v;
    emit();
  }
};

export const initTelegramFullscreen = () => {
  if (initialized) return;
  initialized = true;
  try {
    const tg = window.Telegram?.WebApp;
    if (!tg || !tg.initData) return;
    tgRef = tg;
    try {
      tg.ready();
      tg.expand();
      tg.disableVerticalSwipes?.();
    } catch {}
    try {
      tg.onEvent("fullscreenChanged", applyInset);
      tg.onEvent("contentSafeAreaChanged", applyInset);
      tg.onEvent("safeAreaChanged", applyInset);
    } catch {}
    try {
      if (
        typeof tg.requestFullscreen === "function" &&
        (!tg.isVersionAtLeast || tg.isVersionAtLeast("8.0"))
      ) {
        tg.requestFullscreen();
        fullscreenWanted = true;
      }
    } catch {}
    applyInset();
    // Telegram применяет fullscreen асинхронно — перечитываем inset несколько раз
    [250, 700, 1500, 3000].forEach((ms) => setTimeout(applyInset, ms));
  } catch {}
};

export const subscribeTopInset = (fn) => {
  listeners.add(fn);
  fn(topInset);
  return () => listeners.delete(fn);
};

// --- Подтверждение номера через нативный диалог Telegram (requestContact) ---

export const isTelegramApp = () => {
  try {
    return Boolean(window.Telegram?.WebApp?.initData);
  } catch {
    return false;
  }
};

// Открывает системный диалог «Поделиться номером». Резолвит {phone, userId, response}
// (response — подписанная квитанция для сервера, есть в свежих клиентах).
// Бросает Error("cancelled") при отмене и Error("unsupported") вне Telegram.
export const requestTelegramPhone = () =>
  new Promise((resolve, reject) => {
    const tg = window.Telegram?.WebApp;
    if (!tg || typeof tg.requestContact !== "function") {
      reject(new Error("unsupported"));
      return;
    }
    try {
      tg.requestContact((ok, payload) => {
        const contact = payload?.responseUnsafe?.contact;
        if (!ok || !contact?.phone_number) {
          reject(new Error("cancelled"));
          return;
        }
        resolve({
          phone: contact.phone_number,
          userId: contact.user_id || 0,
          response: payload.response || "",
        });
      });
    } catch {
      reject(new Error("unsupported"));
    }
  });

// Полный цикл: диалог Telegram → серверная проверка подписи → токен сессии + доступ.
export const confirmPhoneViaTelegram = async (name = "") => {
  const tg = window.Telegram?.WebApp;
  if (!tg?.initData) throw new Error("unsupported");
  const contact = await requestTelegramPhone();
  const { data } = await axios.post(
    `${API}/leads/telegram`,
    {
      initData: tg.initData,
      name: name || tg.initDataUnsafe?.user?.first_name || "",
      phone: contact.phone,
      contact_user_id: contact.userId,
      contact_response: contact.response,
      client_key: getClientKey(),
    }
  );
  setUserToken(data.token);
  grantAccess(data.phone);
  return data;
};
